import { HttpParams } from '@angular/common/http';
import {
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganisationTabs } from 'src/app/enums/organisation-tabs';
import { Customer } from 'src/app/interfaces/customer';
import { Update } from 'src/app/interfaces/update';
import { CustomerService } from 'src/app/services/customer.service';
import { DeviceService } from 'src/app/services/device.service';
import { UpdateService } from 'src/app/services/update.service';
import { ApiInit } from 'src/app/utils/api-init';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { UpdateListComponent } from '../../components/update-list/update-list.component';
import { OrganisationProjectsComponent } from './tabs/organisation-projects/organisation-projects.component';
import { OrganisationAboutUsComponent } from './tabs/organisation-about-us/organisation-about-us.component';
import { OrganisationHomeComponent } from './tabs/organisation-home/organisation-home.component';
import { NgIf, NgFor } from '@angular/common';
import { LoaderComponent } from '../../components/loader/loader.component';
import {
  IonContent,
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonRouterOutlet,
  Platform,
  NavController,
} from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-organisation',
  templateUrl: './organisation.page.html',
  standalone: true,
  imports: [
    LoaderComponent,
    NgIf,
    NgFor,
    OrganisationHomeComponent,
    OrganisationAboutUsComponent,
    OrganisationProjectsComponent,
    UpdateListComponent,
    TranslateModule,
    DatePipe,
    IonContent,
    IonBackButton,
    IonSegment,
    IonSegmentButton,
  ],
})
export class OrganisationPage implements OnDestroy {
  public customer: Customer;
  public customerLoaded: boolean = false;
  public currentPage: string = OrganisationTabs.HOME;
  public amountOfFollowers: number = 0;
  public updates: Update[];
  public latestUpdate: Update;
  public totalUpdates: number;
  public updateDeviceData: any = {};
  public deviceDataLoaded = false;
  public language: string;
  public pages = [
    {
      title: 'organisation.tab.home',
      value: OrganisationTabs.HOME,
    },
    {
      title: 'organisation.tab.updates',
      value: OrganisationTabs.UPDATES,
    },
    {
      title: 'organisation.tab.projects',
      value: OrganisationTabs.PROJECTS,
    },
    {
      title: 'organisation.tab.about-us',
      value: OrganisationTabs.ABOUT_US,
    },
  ];
  private backSubscription: Subscription;

  @ViewChild(IonContent) content: IonContent;
  @ViewChild('tabs') projectTabs: ElementRef;
  @ViewChildren(IonSegmentButton) tabButtons: QueryList<IonSegmentButton>;

  constructor(
    private route: ActivatedRoute,
    private apiInit: ApiInit,
    private router: Router,
    private updateService: UpdateService,
    private customerService: CustomerService,
    private deviceService: DeviceService,
    private navCtrl: NavController,
    ionRouterOutlet: IonRouterOutlet,
    platform: Platform
  ) {
    this.language = this.router.getCurrentNavigation()?.extras.state?.language;
    this.apiInit.watch(async () => {
      await this.loadCustomer(this.route.snapshot.params.slug);
    });

    this.backSubscription = platform.backButton.subscribeWithPriority(
      1,
      (processNextHandler) => {
        if (!ionRouterOutlet.canGoBack()) {
          this.navCtrl.setDirection('back');
          this.router.navigate(['/'], { replaceUrl: true });
        } else {
          processNextHandler();
        }
      }
    );
  }

  ngOnDestroy() {
    this.backSubscription.unsubscribe();
  }

  private async loadCustomer(slug: string) {
    try {
      this.customer = await this.customerService.getCustomer(slug);
      this.amountOfFollowers =
        await this.customerService.getCustomerUniqueFollowers(this.customer);
      await this.loadUpdates(this.customer);
      await this.getDeviceData();
    } catch (error) {
      console.error(error);
      this.router.navigate(['404'], { replaceUrl: true });
    }
    this.customerLoaded = true;
    this.fixTabsForAria();
  }

  fixTabsForAria() {
    let initialized = false;
    const interval = setInterval(() => {
      this.tabButtons.forEach((btn) => {
        const el: HTMLElement = (btn as any).el;
        const button = el.shadowRoot.querySelector('button');
        if (!button) return;
        const link = document.createElement('a');
        link.innerText = el.innerText;
        link.className = 'button-native';
        button.replaceWith(link);
        initialized = true;
      });
      if (initialized) clearInterval(interval);
    }, 500);
  }

  private async loadUpdates(customer: Customer) {
    const params = new HttpParams().set('perPage', '21');
    const updateResponse = await this.updateService.getAllUpdatesForCustomer(
      customer,
      params
    );
    this.updates = updateResponse.member;

    const updates = await this.updateService.filterUpdatesByTargetGroup(
      this.updates
    );

    const filteredUpdatesCount = this.updates.length - updates.length;
    this.updates = updates;
    this.totalUpdates = updateResponse.totalItems - filteredUpdatesCount;

    this.updates.some((update) => {
      if (!update.ratingMonitorPush) {
        this.latestUpdate = update;
        return true;
      }
    });
  }

  segmentChanged(event) {
    this.switchTab(event.detail.value);
  }

  async switchTab(tab) {
    const index = this.pages.findIndex((page) => page.value == tab);
    this.currentPage = this.pages[index].value;
    const element = await this.content.getScrollElement();

    if (element.scrollTop > 240) {
      await this.content.scrollToPoint(0, 240);
    }
    const tabElement = this.projectTabs.nativeElement.getElementsByClassName(
      this.currentPage
    )[0];
    if (tabElement) {
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  async getDeviceData() {
    const updateIds = this.updates.map(({ id }) => id).join(',');
    const params = new HttpParams().set('update_ids', updateIds);

    try {
      const response = await this.deviceService.getData(params);
      this.updateDeviceData = response.updates;
    } catch (error) {
      // ignore
    } finally {
      this.deviceDataLoaded = true;
    }
  }
}
